// 11 §11.1 ViewStore — board camera and intent-layer filtering (C4).
import { create } from 'zustand';
import { clampZoom, ZOOM_STEP } from '../../game/board/BoardCamera';
import type { ModifierNote } from '../sim/types';

export type Filter = 'mine' | 'wing' | 'attacks' | 'all';
const FILTERS: Filter[] = ['mine', 'wing', 'attacks', 'all'];

export interface ViewStore {
  /** docs/33 E5, docs/42: the zoom the board camera shows now (CSS px per art px; it eases to the goal) and its scroll. */
  zoom: number; scroll: { x: number; y: number }; filter: Filter;
  /** The zoom the camera is easing to (free-flowing between the bounds), and the screen point (CSS px) that stays put while it does. */
  zoomGoal: number; zoomAnchor?: { x: number; y: number };
  /** Bumped to ask the scene to frame the whole board (`fit`), the selected hero (`hero`) or one unit (`unit`, docs/118 PT8). */
  frame: { kind: 'fit' | 'hero' | 'unit'; seq: number; unit?: number };
  ownerHighlight?: string; hoverHero?: number; minimap: boolean; hidePaths: boolean;
  humans: number;
  /** docs/48 AR4: the hotbar action under the pointer (or keyboard focus); the board previews its range. */
  hoverAction?: number;
  setHoverAction(id?: number): void;
  /** docs/48 AR5: how many units the drawn range lets the action aim at, for the coach line (`undefined`: no range drawn). */
  aimTargets?: number;
  setAimTargets(n?: number): void;
  /** Screen rect of the board viewport (the HUD's centre cell), in CSS px; the scene fits and centres the board in it. */
  viewport?: { x: number; y: number; w: number; h: number };
  /** The room's time of day and weather as the scene lit it (docs/19, docs/20), for the HUD. */
  sky?: { hour: number; phase: string; weather: string };
  setSky(sky?: { hour: number; phase: string; weather: string }): void;
  /** docs/26: what this fight's ground and sky do to movement and sight, and the selected hero's footing. */
  field?: FieldNotes;
  setField(field?: FieldNotes): void;
  setViewport(r: { x: number; y: number; w: number; h: number }): void;
  /** The scene publishes the live camera. */
  setCamera(zoom: number, x: number, y: number): void;
  /** Ease to a zoom (`anchor`: the screen point to keep under the pointer); `zoomBy` scales by one step. */
  setZoom(z: number, anchor?: { x: number; y: number }): void; zoomBy(delta: 1 | -1, anchor?: { x: number; y: number }): void;
  setScroll(x: number, y: number): void; frameBoard(kind: 'fit' | 'hero'): void;
  /** docs/118 PT8: ease the camera over to a unit (a double-click on its roster card). */
  frameUnit(unitId: number): void;
  setFilter(f: Filter): void; cycleFilter(): void;
  setOwnerHighlight(owner?: string): void; setHoverHero(heroId?: number): void;
  toggleMinimap(): void; setHidePaths(v: boolean): void;
  /** docs/40: the encounter's inventory drawer (I) and the haul list under the haul chip. */
  inventory: boolean; haulOpen: boolean;
  toggleInventory(open?: boolean): void; toggleHaul(open?: boolean): void;
}

/** docs/26 + docs/29: the field's conditions, the encounter modifiers in force, and the selected hero's footing. */
export interface FieldNotes {
  conditions: string[]; hero?: string; traits: string[]; sight: number; modifiers: ModifierNote[]; windShift: [number, number, number];
  /** docs/93 AQ10: how the hero moves, when it is not plainly on foot ("Swims: …"), and what the water is doing to it. */
  moves?: string[];
}

export const useView = create<ViewStore>()((set, get) => ({
  zoom: 3, zoomGoal: 3, frame: { kind: 'fit', seq: 0 }, scroll: { x: 0, y: 0 }, filter: 'all', minimap: false, hidePaths: false, humans: 4,
  inventory: false, haulOpen: false,
  toggleInventory(open) { set({ inventory: open ?? !get().inventory }); },
  toggleHaul(open) { set({ haulOpen: open ?? !get().haulOpen }); },
  setSky(sky) { const c = get().sky; if (sky?.hour !== c?.hour || sky?.weather !== c?.weather || sky?.phase !== c?.phase) set({ sky }); },
  setField(field) { if (JSON.stringify(field) !== JSON.stringify(get().field)) set({ field }); },
  setViewport(viewport) { const v = get().viewport; if (!v || v.x !== viewport.x || v.y !== viewport.y || v.w !== viewport.w || v.h !== viewport.h) set({ viewport }); },
  setCamera(zoom, x, y) { const c = get(); if (c.zoom !== zoom || c.scroll.x !== x || c.scroll.y !== y) set({ zoom, scroll: { x, y } }); },
  setZoom(z, anchor) { set({ zoomGoal: clampZoom(z), zoomAnchor: anchor }); },
  zoomBy(delta, anchor) { set({ zoomGoal: clampZoom(get().zoomGoal * ZOOM_STEP ** delta), zoomAnchor: anchor }); },
  setScroll(x, y) { set({ scroll: { x, y } }); },
  frameBoard(kind) { set({ frame: { kind, seq: get().frame.seq + 1 } }); },
  frameUnit(unit) { set({ frame: { kind: 'unit', unit, seq: get().frame.seq + 1 } }); },
  setFilter(filter) { set({ filter }); },
  cycleFilter() { const i = FILTERS.indexOf(get().filter); set({ filter: FILTERS[(i + 1) % FILTERS.length]! }); },
  setOwnerHighlight(ownerHighlight) { set({ ownerHighlight }); },
  setHoverHero(hoverHero) { set({ hoverHero }); },
  setHoverAction(hoverAction) { if (get().hoverAction !== hoverAction) set({ hoverAction }); },
  setAimTargets(aimTargets) { if (get().aimTargets !== aimTargets) set({ aimTargets }); },
  toggleMinimap() { set({ minimap: !get().minimap }); },
  setHidePaths(hidePaths) { set({ hidePaths }); },
}));
